import { asArray, isDateLess, sumOf } from './promoPrimitives';
import { getOffer, refOfOfferInfo, toRef } from './promoModel';
import { isCampaignInNegoPeriod } from './promoPhase';
import { isRatedOnNego } from './promoForecast';
import { localProductPaying, maxFreeFor, payingQtyFor } from './promoChecks';
import { checkLocalOfferRules } from './promoLocalOffer';
import { totalFree, totalPaying } from './promoUnits';
import { photosOf } from './promoPhotos';

// Validation of the promo step (#23521) : the single gate deciding whether the visit can be closed (lesson 80).
// Port of `visitStep3PromoVM.isValid` (visitStep3-promo.js:1465-1846), DOM effects excluded : it returns the errors,
// each with the message the legacy displayed next to the field (`display`), and the step message the legacy kept
// (`message` : the last error raised, same order as the legacy loops).
// A message descriptor is `{ key, prefix?, suffix?, params? }`, formatted with `formatPromoMessage`.

/** `prefix + t(key, params) + suffix`, the way the legacy concatenated its messages. */
export function formatPromoMessage(descriptor, t) {
	if (!descriptor) return '';
	return `${descriptor.prefix || ''}${t(descriptor.key, ...(descriptor.params || []))}${descriptor.suffix || ''}`;
}

const max = (n) => ` (Max: ${n})`;
const min = (n) => ` (Min: ${n})`;
const unitKey = (offer, ub, uc) => (offer?.displayUnit === 1 ? ub : uc);

function validateLocalOffers(snapshot, report) {
	asArray(snapshot?.model?.offerInfos)
		.filter((oi) => oi.local === true)
		.forEach((offerInfo) => {
			const campaign = asArray(snapshot.campaigns).find((c) => c.id === offerInfo.campaignId);
			if (!campaign || !asArray(campaign.localOfferKeys).includes(offerInfo._key)) return;
			const offer = getOffer(campaign, offerInfo.campaignOfferId);
			const ref = refOfOfferInfo(offerInfo);
			const scope = { campaignId: campaign.id, offerKey: offerInfo._key, campaignOfferId: offerInfo.campaignOfferId };
			const rows = asArray(offerInfo._negociatedCampaignOffers);
			const summary = { key: 'MandatoryLocaleOffers' };

			const rules = checkLocalOfferRules(snapshot, campaign, offerInfo);
			if (rules.overlapping && !rules.overlappingError) report.warnings.push({ ...scope, level: 'offer', display: { key: 'OfferOverlapping', params: [rules.overlapping] } });
			if (rules.hasError) {
				const display = rules.emptyOffer ? { key: 'OfferEmptyError', params: [rules.overlapping] } : rules.overlappingError ? { key: 'OfferOverlapping', params: [rules.overlapping] } : { key: 'OfferDatesError' };
				report.add({ ...scope, level: 'offer', code: 'LocalOfferRules', display, rules }, summary);
			}

			// In its statement the negotiation is frozen : like a national offer, its quantity rules no longer block (#23572).
			if (!isRatedOnNego(snapshot, campaign, offerInfo)) return;

			rows.filter((row) => row.additional === false && row.isFree === false).forEach((nego) => {
				const negoOffer = getOffer(campaign, nego.campaignOfferId);
				const product = asArray(negoOffer?.products).find((p) => p.productId === nego.productId);
				if (!product) return;
				const ub = offer?.displayUnit === 1;
				const total = localProductPaying(offerInfo, nego.productId, ub, nego.offerKey);
				const outOfRange = (product.minUC && total < product.minUC) || (product.maxUC && total > product.maxUC);
				if (total !== 0 && !outOfRange) return;
				const display = !outOfRange ? null : !product.minUC || total >= product.minUC ? { key: ub ? 'PayingUBMaxExceeded' : 'PayingUCMaxExceeded', suffix: max(product.maxUC) } : { key: ub ? 'PayingUBMinNotReached' : 'PayingUCMinNotReached', suffix: min(product.minUC) };
				report.add({ ...scope, level: 'product', productId: nego.productId, code: outOfRange ? 'PayingProductRange' : 'PayingProductEmpty', display }, summary);
			});

			const checkedFree = [];
			rows.filter((row) => row.additional === false && row.isFree === true).forEach((nego) => {
				if (checkedFree.includes(nego.productId)) return;
				checkedFree.push(nego.productId);
				const negoOffer = getOffer(campaign, nego.campaignOfferId);
				if (!asArray(negoOffer?.products).find((p) => p.productId === nego.productId)) return;
				const items = rows.filter((row) => row.campaignId === nego.campaignId && row.offerKey === nego.offerKey && row.productId === nego.productId && row.isFree === true);
				if (sumOf(items, negoOffer.displayUnit === 1 ? 'volume' : 'volumeUC') === 0) report.add({ ...scope, level: 'product', productId: nego.productId, code: 'FreeProductEmpty', free: true, display: null }, summary);
			});

			if (!offer) return;
			const ub = offer.displayUnit === 1;
			if (offer.maxFreeQty > 0 && totalFree(rows, ref, ub) > offer.maxFreeQty) {
				const key = unitKey(offer, 'FreeUBMaxExceeded', 'FreeUCMaxExceeded');
				const freeProducts = rows.filter((row) => row.additional === false && row.isFree === true).map((row) => row.productId);
				report.add({ ...scope, level: 'offer', code: key, freeProducts, display: { key, suffix: max(offer.maxFreeQty) } }, { prefix: `${campaign.label} / ${offer.label} - `, key, suffix: max(offer.maxFreeQty) });
			}

			rows.filter((row) => row.additional === false && row.isFree === true).forEach((freeRow) => {
				const offerProduct = asArray(offer.products).find((p) => p.productId === freeRow.productId);
				if (!offerProduct || !offerProduct.pctGratuity) return;
				const limit = maxFreeFor(payingQtyFor(rows, freeRow, offer), offerProduct.pctGratuity);
				const freeQty = ub ? freeRow.volume : freeRow.volumeUC;
				if (freeQty <= limit) return;
				const key = unitKey(offer, 'FreeUBPctExceeded', 'FreeUCPctExceeded');
				report.add({ ...scope, level: 'product', productId: freeRow.productId, free: true, code: key, display: { key, suffix: max(limit) } }, { key, suffix: max(limit) });
			});

			const paying = totalPaying(rows, ref, ub);
			const minOk = !offer.minUC || paying >= offer.minUC;
			const maxOk = !offer.maxUC || paying <= offer.maxUC;
			if (!minOk || !maxOk) {
				const display = !minOk ? { key: unitKey(offer, 'PayingUBMinNotReached', 'PayingUCMinNotReached'), suffix: min(offer.minUC) } : { key: unitKey(offer, 'PayingUBMaxExceeded', 'PayingUCMaxExceeded'), suffix: max(offer.maxUC) };
				report.add({ ...scope, level: 'offer', code: 'PayingOfferRange', display }, display);
			}
		});
}

function validateNationalOffers(snapshot, report) {
	asArray(snapshot?.campaigns).forEach((campaign) => {
		if (campaign.promoLocal || !isCampaignInNegoPeriod(campaign)) return;
		asArray(campaign.offers).forEach((offer) => {
			const ref = toRef(campaign.id, offer.id);
			const scope = { campaignId: campaign.id, offerKey: null, campaignOfferId: offer.id };
			const prefix = `${campaign.label} / ${offer.label} - `;
			const ub = offer.displayUnit === 1;
			const rows = asArray(snapshot.model?.negotiated).filter((row) => row.campaignId === campaign.id && row.campaignOfferId === offer.id);
			const freeRows = rows.filter((row) => !row.additional && row.isFree);

			if (offer.maxFreeQty > 0 && totalFree(rows, ref, ub) > offer.maxFreeQty) {
				const key = unitKey(offer, 'FreeUBMaxExceeded', 'FreeUCMaxExceeded');
				report.add({ ...scope, level: 'offer', code: key, freeProducts: freeRows.map((row) => row.productId), display: { key, suffix: max(offer.maxFreeQty) } }, { prefix, key, suffix: max(offer.maxFreeQty) });
			}

			freeRows.forEach((freeRow) => {
				const offerProduct = asArray(offer.products).find((p) => p.productId === freeRow.productId);
				if (!offerProduct || !offerProduct.pctGratuity) return;
				const limit = maxFreeFor(payingQtyFor(rows, freeRow, offer, ref), offerProduct.pctGratuity);
				const freeQty = ub ? freeRow.volume : freeRow.volumeUC;
				if (freeQty <= limit) return;
				const key = unitKey(offer, 'FreeUBPctExceeded', 'FreeUCPctExceeded');
				report.add({ ...scope, level: 'product', productId: freeRow.productId, free: true, code: key, display: { key, suffix: max(limit) } }, { key, suffix: max(limit) });
			});

			const checkedFree = [];
			freeRows.forEach((freeRow) => {
				if (checkedFree.includes(freeRow.productId)) return;
				checkedFree.push(freeRow.productId);
				const total = sumOf(
					freeRows.filter((row) => row.productId === freeRow.productId),
					ub ? 'volume' : 'volumeUC'
				);
				if (total === 0) report.add({ ...scope, level: 'product', productId: freeRow.productId, free: true, code: 'FreeProductEmpty', display: null }, { prefix, key: 'Mandatory' });
			});

			const paying = totalPaying(rows, ref, ub);
			const minOk = !offer.minUC || paying >= offer.minUC || paying === 0;
			const maxOk = !offer.maxUC || paying <= offer.maxUC;
			if (!minOk || !maxOk) {
				const display = !minOk ? { key: unitKey(offer, 'PayingUBMinNotReached', 'PayingUCMinNotReached'), suffix: min(offer.minUC) } : { key: unitKey(offer, 'PayingUBMaxExceeded', 'PayingUCMaxExceeded'), suffix: max(offer.maxUC) };
				report.add({ ...scope, level: 'offer', code: 'PayingOfferRange', display }, { ...display, prefix });
			}

			const payingRows = rows.filter((row) => !row.additional && !row.isFree);
			const checked = [];
			asArray(offer.products).forEach((op) => {
				if (checked.includes(op.productId)) return;
				checked.push(op.productId);
				if (!op.minUC && !op.maxUC) return;
				const productPaying = sumOf(
					payingRows.filter((row) => row.productId === op.productId),
					ub ? 'volume' : 'volumeUC'
				);
				const pMinOk = !op.minUC || productPaying >= op.minUC || productPaying === 0;
				const pMaxOk = !op.maxUC || productPaying <= op.maxUC;
				if (pMinOk && pMaxOk) return;
				const display = !pMinOk ? { key: unitKey(offer, 'PayingProductUBMinNotReached', 'PayingProductUCMinNotReached'), suffix: min(op.minUC) } : { key: unitKey(offer, 'PayingProductUBMaxExceeded', 'PayingProductUCMaxExceeded'), suffix: max(op.maxUC) };
				report.add({ ...scope, level: 'product', productId: op.productId, code: 'PayingProductRange', display }, display);
			});
		});
	});
}

const hasPresentStatement = (rows) => asArray(rows).some((row) => !row.additional && row.present > 0);

function validatePhotos(snapshot, report) {
	asArray(snapshot?.campaigns).forEach((campaign) => {
		if (!campaign.photoStatementMandatory) return;
		const error = { campaignId: campaign.id, offerKey: null, level: 'campaign', code: 'MandatoryPromoPhotos', display: { key: 'MandatoryPromoPhotos' } };
		if (!campaign.promoLocal) {
			const active = !isCampaignInNegoPeriod(campaign) && campaign.photoStatementActive;
			const stated = asArray(snapshot.model?.stated).filter((row) => row.campaignId === campaign.id);
			if (active && !photosOf(snapshot, campaign.id).length && hasPresentStatement(stated)) report.add(error, error.display);
			return;
		}
		asArray(snapshot.model?.offerInfos)
			.filter((oi) => oi.campaignId === campaign.id)
			.forEach((offerInfo) => {
				const active = isDateLess(offerInfo.startDate, snapshot.now, true) && campaign.photoStatementActive;
				if (!active || asArray(offerInfo.photosStatements).length || !hasPresentStatement(offerInfo._statedCampaignOffers)) return;
				if (report.errors.some((e) => e.campaignId === campaign.id)) return;
				report.add({ ...error, offerKey: offerInfo._key }, error.display);
			});
	});
}

/**
 * Validates the promo step. Read-only (preparation, consulted visit) is always valid.
 * @returns {{ isValid: boolean, errors: Array, warnings: Array, message: (object|null) }}
 */
export function validateStep(snapshot) {
	const report = {
		errors: [],
		warnings: [],
		message: null,
		add(error, summary) {
			this.errors.push(error);
			if (summary) this.message = summary;
		},
	};
	if (snapshot?.readonly) return { isValid: true, errors: [], warnings: [], message: null };

	validateLocalOffers(snapshot, report);
	validateNationalOffers(snapshot, report);
	validatePhotos(snapshot, report);

	return { isValid: report.errors.length === 0, errors: report.errors, warnings: report.warnings, message: report.message };
}

/** Errors of one offer (offer and product level), for the offer card and the order buttons. */
export const offerErrors = (validation, ref) => asArray(validation?.errors).filter((e) => e.campaignId === ref.campaignId && (ref.offerKey ? e.offerKey === ref.offerKey : e.campaignOfferId === ref.campaignOfferId && !e.offerKey) && e.level !== 'campaign');

/** Errors of one product line (`free` : the free line). */
export const productErrors = (validation, ref, productId, free = false) => offerErrors(validation, ref).filter((e) => (e.level === 'product' && e.productId === productId && !!e.free === free) || (free && e.freeProducts?.includes(productId)));
